import React, { useEffect, useState } from "react";
import {
    Box as MuiBox,
    Typography as MuiTypography,
    IconButton as MuiIconButton,
    Paper as MuiPaper,
    Tooltip as MuiTooltip
} from "@mui/material";
import { ArrowBack as ArrowBackIcon } from "@mui/icons-material";
import { WorkProduct, Feature, Initiative } from "../types";
// Import AI Assistant icon from navigation
import AiChatIcon from "../imports/Icon-2004-80";

import DocSectionContainer from "../../../components/Canvas/DocSections/DocSectionContainer";
import DocumentSectionsList from "./DocumentSectionsList";
import CollabAvatarStack from "@/collab/CollabAvatarStack";
import PointerOverlay from "@/collab/PointerOverlay";
import { sectionField } from "@/collab/roomName";
import { deriveDocScope } from "@/components/Canvas/DocSections/docScope";
import { ViewOnlyIndicator } from "@/security/access";
import { isGeneratedDocumentWorkProduct } from "../utils/workProductEditorSurface";

// Visibility flags for features awaiting backend support.
// Flip to true once the backend can report document status and presence.
const SHOW_STATUS_PILL = false;

// Design tokens (light surface) — sourced from
// app/frontend/docs/v1 Phase 2/editor/assets/intelligenic-tokens.css.
const T = {
    bgPaper: "#ffffff",
    bgInput: "#f3f3f5",
    bgPanel: "#f8fafc",
    divider: "rgba(0, 0, 0, 0.07)",
    border: "rgba(0, 0, 0, 0.07)",
    fg1: "#111827",
    fg2: "#374151",
    fg3: "#4b5563",
    fg4: "#6b7280",
    warning: "#d97706"
};

// Short document-type label for the breadcrumb (e.g., "HLD" for Markdown work products).
const docTypeLabel = (wp: WorkProduct): string => {
    if (isGeneratedDocumentWorkProduct(wp)) return "Doc";
    switch (wp.content_type) {
        case "CODE":
            return "Code";
        case "DIAGRAM":
            return "Diagram";
        case "WIREFRAME":
            return "Wireframe";
        case "MARKDOWN":
        case "TEXT":
        default:
            return "Doc";
    }
};

interface WorkProductEditorProps {
    workProduct: WorkProduct | null;
    onBack: () => void;
    onJiraSyncOpen?: (context: "workproduct") => void;
    onAIChatToggle?: () => void;
    AIChatToggled?: boolean;
    selectedProject?: Initiative | null;
    selectedFeature?: Feature | null;
    onRebuildStart?: (workProductId: string, projectId?: number, featureId?: string | number, userStoryId?: string) => void;
    onProgressUpdate?: (workProductId: string, projectId?: number, featureId?: string | number, userStoryId?: string, update?: { progress?: number; message?: string }) => void;
    onRebuildJobComplete?: (workProductId: string, projectId?: number, featureId?: string | number, userStoryId?: string) => void;
    /** Navigate to the Integrations view — wired to SyncPanel's "Reconnect" CTA (see Task 16). */
    onNavigateToIntegrations?: () => void;
    readOnly?: boolean;
    canGenerate?: boolean;
}

const WorkProductEditor_Text: React.FC<WorkProductEditorProps> = ({ workProduct, onBack, onAIChatToggle, AIChatToggled, selectedProject, selectedFeature, onRebuildStart, onProgressUpdate, onRebuildJobComplete, onNavigateToIntegrations, readOnly = true, canGenerate = false }) => {
    const [selectedSectionIndex, setSelectedSectionIndex] = useState(0);
    const [selectedSection, setSelectedSection] = useState<any>(null);

    useEffect(() => {
        if (workProduct) {
            console.log("WorkProductEditor: Loading new work product", {
                id: workProduct.id,
                name: workProduct.name,
                level: workProduct.level,
                user_story_id: workProduct.user_story_id,
                fullObject: workProduct
            });
        }
        setSelectedSectionIndex(0);
        setSelectedSection(null);
    }, [workProduct]);

    if (!workProduct) {
        return (
            <MuiBox
                sx={{
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    bgcolor: "background.default"
                }}
            >
                <MuiTypography variant="h6" color="text.secondary">
                    No work product selected
                </MuiTypography>
            </MuiBox>
        );
    }

    const handleSectionChange = (index: number, section: any) => {
        setSelectedSectionIndex(index);
        setSelectedSection(section);
    };

    const handleAIAssistantClick = () => {
        if (onAIChatToggle) {
            onAIChatToggle();
        }
    };

    const productName = selectedProject?.name || selectedProject?.project_name || "Product";
    const documentName = docTypeLabel(workProduct);
    const sectionName: string = selectedSection?.section_title || workProduct.name || "";
    const editorScope = deriveDocScope(workProduct, selectedFeature?.id || (selectedFeature as any)?.featureid, undefined);
    const scopedFeatureId = editorScope.featureId || undefined;
    const scopedUserStoryId = editorScope.userStoryId;

    return (
        <MuiBox
            sx={{
                height: "100vh",
                display: "flex",
                flexDirection: "column",
                bgcolor: "background.default"
            }}
        >
            {/* Title bar — V1 Editor design (breadcrumb pill + status + collaborators). */}
            <MuiPaper
                elevation={0}
                sx={{
                    bgcolor: T.bgPaper,
                    borderBottom: `1px solid ${T.divider}`,
                    px: "26px",
                    height: 64,
                    minHeight: 64,
                    maxHeight: 64,
                    display: "flex",
                    alignItems: "center",
                    gap: "14px",
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                    flexShrink: 0
                }}
            >
                {/* Back chevron + breadcrumb pill */}
                <MuiBox sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
                    <MuiTooltip title="Back" placement="bottom" arrow>
                        <MuiIconButton
                            onClick={onBack}
                            data-ui-bridge="btn.back-to-feature-studio"
                            data-ui-label="Back to Feature Studio"
                            data-ui-role="button"
                            data-ui-group="navigation"
                            data-ui-description="Return to Feature Studio from the editor"
                            sx={{
                                width: 30,
                                height: 30,
                                border: `1px solid ${T.border}`,
                                bgcolor: T.bgPaper,
                                borderRadius: "8px",
                                color: T.fg2,
                                "&:hover": { bgcolor: T.bgInput }
                            }}
                        >
                            <ArrowBackIcon sx={{ fontSize: 16 }} />
                        </MuiIconButton>
                    </MuiTooltip>

                    <MuiBox
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            px: "10px",
                            py: "5px",
                            borderRadius: "999px",
                            bgcolor: T.bgInput,
                            fontSize: 12,
                            minWidth: 0
                        }}
                    >
                        <MuiTypography component="span" sx={{ color: T.fg4, fontSize: 12, fontWeight: 500 }}>
                            {productName}
                        </MuiTypography>
                        <MuiBox component="span" sx={{ color: T.fg4, opacity: 0.5 }}>›</MuiBox>
                        <MuiTypography component="span" sx={{ color: T.fg3, fontSize: 12, fontWeight: 500 }}>
                            {documentName}
                        </MuiTypography>
                        {sectionName && (
                            <>
                                <MuiBox component="span" sx={{ color: T.fg4, opacity: 0.5 }}>›</MuiBox>
                                <MuiTypography
                                    component="span"
                                    sx={{
                                        color: T.fg1,
                                        fontSize: 12,
                                        fontWeight: 600,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        maxWidth: 280
                                    }}
                                    title={sectionName}
                                >
                                    {sectionName}
                                </MuiTypography>
                            </>
                        )}
                    </MuiBox>
                </MuiBox>

                <MuiBox sx={{ flex: 1 }} />

                {/* Status pill — hidden until backend reports document/section state. */}
                {SHOW_STATUS_PILL && (
                    <MuiBox
                        sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            px: "9px",
                            py: "4px",
                            borderRadius: "999px",
                            bgcolor: "rgba(217, 119, 6, 0.08)",
                            border: "1px solid rgba(217, 119, 6, 0.18)",
                            color: T.warning,
                            fontSize: 11,
                            fontWeight: 600
                        }}
                    >
                        <MuiBox sx={{ width: 6, height: 6, borderRadius: 3, bgcolor: T.warning }} />
                        In progress
                    </MuiBox>
                )}

                {/* Live collaborator presence (renders nothing unless collab is active). */}
                <CollabAvatarStack />

                {readOnly && <ViewOnlyIndicator />}

                {/* AI Assistant — preserved verbatim; the chat component owns its own behaviour. */}
                {AIChatToggled === false && !readOnly && canGenerate && (
                    <MuiTooltip title="AI Assistant">
                        <MuiIconButton
                            onClick={handleAIAssistantClick}
                            size="small"
                            sx={{
                                bgcolor: "primary.main",
                                color: "primary.contrastText",
                                borderRadius: "50%",
                                width: 40,
                                height: 40,
                                "&:hover": {
                                    bgcolor: "primary.dark",
                                    color: "primary.contrastText",
                                    transform: "scale(1.05)"
                                },
                                "&:active": { transform: "scale(0.95)" },
                                transition: "all 0.2s ease-in-out"
                            }}
                        >
                            <MuiBox
                                sx={{
                                    width: 24,
                                    height: 24,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    color: "inherit"
                                }}
                            >
                                <AiChatIcon />
                            </MuiBox>
                        </MuiIconButton>
                    </MuiTooltip>
                )}

            </MuiPaper>

            {/* App.Body.TwoColumn Layout */}
            <MuiBox sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
                {/* Column.Navigation — flat card list of document sections (240px). */}
                <MuiPaper
                    elevation={0}
                    sx={{
                        width: 240,
                        flexShrink: 0,
                        borderRadius: 0,
                        bgcolor: T.bgPanel,
                        display: "flex",
                        flexDirection: "column",
                        overflow: "hidden",
                        borderRight: `1px solid ${T.divider}`
                    }}
                >
                    <MuiBox sx={{ flex: 1, overflow: "auto" }}>
                        {workProduct &&
                            (() => {
                                const parsedId = typeof workProduct.id === "string" ? parseInt(workProduct.id, 10) : workProduct.id;
                                const validId = !isNaN(parsedId) && parsedId > 0 ? parsedId : 0;

                                if (validId === 0) {
                                    console.warn("WorkProductEditor: Invalid work product ID", workProduct.id);
                                }

                                return <DocumentSectionsList workProductId={validId} onSectionChange={handleSectionChange} readOnly={readOnly} />;
                            })()}
                    </MuiBox>
                </MuiPaper>

                {/* Editor column — TipTap fills the container. */}
                <MuiBox
                    sx={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        minWidth: 0,
                        minHeight: 0,
                        bgcolor: T.bgPaper,
                        overflow: "hidden",
                        position: "relative"
                    }}
                >
                    <DocSectionContainer
                        workProduct={workProduct}
                        projectId={(() => {
                            const parsed = Number(selectedProject?.id);
                            return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
                        })()}
                        featureId={scopedFeatureId}
                        userStoryId={scopedUserStoryId}
                        hasJiraLink={selectedProject?.external_link_type === "JIRA"}
                        hasAzureDevOpsLink={selectedProject?.external_link_type === "AZURE_DEVOPS"}
                        activeTabIndex={selectedSectionIndex}
                        activeSection={selectedSection}
                        onRebuildComplete={onBack}
                        onRebuildStart={onRebuildStart}
                        onProgressUpdate={onProgressUpdate}
                        onRebuildJobComplete={onRebuildJobComplete}
                        onNavigateToIntegrations={onNavigateToIntegrations}
                        readOnly={readOnly}
                        canRebuild={!readOnly && canGenerate}
                    />
                    <PointerOverlay activeField={selectedSection?.warehouse_id ? sectionField(selectedSection.warehouse_id) : undefined} />
                </MuiBox>
            </MuiBox>
        </MuiBox>
    );
};

export default WorkProductEditor_Text;
